/**
 * AvatarUploader — the click-to-upload affordance on the profile cover.
 *
 * Three-phase flow (matches the API):
 *   1. SPA validates the picked file (size + mime) before talking to the API.
 *   2. SPA calls `identity.requestAvatarUpload` → presigned PUT URL + key.
 *   3. SPA `fetch(uploadUrl, { method: 'PUT', body: file })` directly to S3.
 *   4. SPA calls `identity.confirmAvatar` with the key → DB flips, audit row.
 *   5. SPA invalidates `identity.me` so the topbar avatar refreshes.
 *
 * The state machine itself lives in `useS3Upload` (lib/) — second usage
 * of the pattern (logo upload is the other) so it's been lifted there.
 * This component owns only the visual chip + the wiring of the hook to
 * the avatar-specific mutations and copy.
 *
 * Failure modes the user can hit:
 *   - File too large / wrong type → inline error, no API call.
 *   - 412 PRECONDITION_FAILED ("storage-disabled") → "Uploads are not
 *     available right now." (S3 not configured in dev — common path).
 *   - PUT to S3 fails (network, CORS) → generic upload error.
 *   - Confirm fails → upload landed but DB didn't update; the user can
 *     retry by re-clicking. We do NOT auto-retry confirm because the
 *     mutation might have actually succeeded server-side (the response
 *     just got lost) — the idempotent confirm handles that case.
 *
 * Keyboard / a11y: the avatar is wrapped in a `<button>`, focusable and
 * activated by Enter/Space. The hidden file input is triggered programmatically
 * so the visual chip is the only affordance the user perceives.
 */
import { Camera } from "lucide-react";
import { toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { meQueryKey, type MeUser } from "../../lib/session.ts";
import { useS3Upload, type S3UploadStatus } from "../../lib/use-s3-upload.ts";
import { UserAvatar } from "../workspace/index.ts";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_BYTES = 2 * 1024 * 1024; // 2 MiB — must match server.

/** Re-exported for callers (cover) that render the status helper line. */
export type AvatarUploadStatus = S3UploadStatus;

export interface AvatarUploaderProps {
  user: MeUser;
  /**
   * Notified whenever the upload state changes — the cover uses this to
   * render the helper / progress / error line in its footer (which is
   * full-width, not constrained to the avatar's column width). Stripping
   * the line out of this component keeps the avatar tile tight.
   */
  onStatusChange?: (status: AvatarUploadStatus) => void;
}

export function AvatarUploader({ user, onStatusChange }: AvatarUploaderProps) {
  const requestUpload = trpc.identity.requestAvatarUpload.useMutation();
  const confirm = trpc.identity.confirmAvatar.useMutation();

  const { isBusy, openPicker, inputRef, inputProps } = useS3Upload({
    request: requestUpload as never,
    confirm,
    allowedTypes: ALLOWED_TYPES,
    maxBytes: MAX_BYTES,
    invalidateQueries: [meQueryKey],
    copy: {
      invalidType: "Use a JPEG, PNG, or WebP image.",
      tooLarge: "Image must be under 2 MB.",
    },
    onSuccess: () => {
      // Top-right toast — the avatar swap in the topbar is easy to miss,
      // and there's no other visible confirmation the save committed.
      // Matches the LogoUploader treatment for parity.
      toast.success("Photo saved", {
        description: "Your new profile photo is live across the workspace.",
      });
    },
    ...(onStatusChange ? { onStatusChange } : {}),
  });

  return (
    <>
      {/* The button wraps the avatar and overlays a hover-revealed camera
          chip in the bottom-right corner. The chip is always visible on
          touch (no hover state), so on mobile the affordance is obvious.
          No helper text here — the cover renders that in its full-width
          footer so the avatar column stays at its natural 96px width. */}
      <button
        type="button"
        onClick={openPicker}
        disabled={isBusy}
        aria-label="Change profile photo"
        className="group relative inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper rounded-sm disabled:opacity-70"
      >
        <UserAvatar name={user.name} src={user.avatarUrl} size="xl" />
        <span
          className="absolute -bottom-1 -right-1 inline-grid place-items-center h-7 w-7 rounded-sm bg-ink text-paper opacity-80 group-hover:opacity-100 transition-opacity shadow-card ring-2 ring-paper"
          aria-hidden
        >
          <Camera size={13} strokeWidth={2.5} />
        </span>
      </button>

      <input ref={inputRef} className="sr-only" {...inputProps} />
    </>
  );
}

/**
 * Convenience: turn an `AvatarUploadStatus` into the line of text the
 * cover footer renders. Lives next to the component because the mapping
 * is shared between the only consumer (the cover) and any future caller
 * that wants to mirror the same wording.
 */
export function avatarStatusMessage(status: AvatarUploadStatus): {
  text: string;
  tone: "muted" | "bad";
} {
  if (status.kind === "uploading") {
    return {
      text:
        status.phase === "request"
          ? "Preparing upload…"
          : status.phase === "put"
            ? "Uploading photo…"
            : "Saving…",
      tone: "muted",
    };
  }
  if (status.kind === "error") {
    return { text: status.message, tone: "bad" };
  }
  if (status.kind === "saved") {
    return { text: "Photo saved · just now", tone: "muted" };
  }
  return { text: "Photo: JPEG, PNG, or WebP · up to 2 MB", tone: "muted" };
}
